/**
 * @author       julian@zumper.com (Julian Napolitan)
 * @copyright    Copyright (c) 2021, Zumper
 */

import type { SignInUrl, SignUpUrl } from '@zcode-ui/account'
import { ChevronLeft, ZumperFullDark } from '@zcode-ui/icons'
import {
  Dialog,
  DialogContentWithOverlayAndCloseButton,
  DialogHeader,
  DialogTitle,
} from '@zcode-ui/shadz-ui'
import { useAnalyticsTrack } from '@zcode/nr/es/shared/packages/analytics-track'
import React, { useCallback, useState } from 'react'
import { useSelector } from 'react-redux'
import { selectIsBot } from '../../module/selectors'
import type { OnNavLinkClickProps, SubmenuType } from '../../types'
import { getOrigin } from '../../utils/getOrigin'
import { getPrevSubmenu } from '../../utils/submenu'
import { ModalBodyContentLoadable } from './ModalBodyContentLoadable'

interface MobileMenuProps {
  isCurrentSearchView?: boolean
  isDetailView?: boolean
  isHomepageView?: boolean
  isLoggedIn?: boolean
  isMessage3?: boolean
  onClose?: () => void
  onLoginClick?: () => void
  onLogoutClick?: () => void
  onNavLinkClick?: (props: OnNavLinkClickProps) => void
  /** passed by for you, but not by the 'normal' header in NavbarContainer */
  onSignupClick?: () => void
  showModal?: boolean
  signInHref: SignInUrl
  signUpHref: SignUpUrl
  toolsActivated?: boolean
  unreadCount?: number
}

const titles: Record<SubmenuType, string> = {
  smAFR: 'Apartments for rent',
  smAFRCheap: 'Cheap Apartments',
  smAFRLuxury: 'Luxury Apartments',
  smAFRPetFriendly: 'Pet-friendly Apartments',
  smAFRShortTerm: 'Short Term Rentals',
  smAmenities: 'Amenities',
  smHFR: 'Houses for rent',
  smMain: 'Menu',
  smRentals: 'Rentals',
  smResources: 'Rental resources',
  smRFR: 'Rooms for rent',
}

export const MobileMenu = ({
  onLoginClick,
  onLogoutClick,
  onNavLinkClick,
  onClose,
  onSignupClick,
  showModal = false,
  signInHref,
  signUpHref,
  unreadCount,
  isHomepageView,
  isCurrentSearchView,
  isDetailView,
  isMessage3,
}: MobileMenuProps) => {
  const close = () => {
    onClose?.()
    onCloseAnalytics()
  }
  const origin = getOrigin(isHomepageView, isCurrentSearchView, isDetailView)
  const onCloseAnalytics = useAnalyticsTrack('Navbar', 'Close', origin)
  const [activeSubmenu, setActiveSubmenu] = useState<SubmenuType>('smMain')
  const showPrevMenu = () => setActiveSubmenu(getPrevSubmenu(activeSubmenu))
  const isBot = useSelector(selectIsBot)

  const handleSetActiveSubmenu = useCallback(
    (navLinkClickProps: OnNavLinkClickProps, submenu: SubmenuType) => {
      onNavLinkClick?.(navLinkClickProps)
      setActiveSubmenu(submenu)
    },
    [onNavLinkClick]
  )

  const withCityTracking = (trackAction: string) => {
    return (name: string, href: string) => {
      close()
      setActiveSubmenu('smMain')
      onNavLinkClick?.({
        trackAction,
        trackCity: name,
        linkClicked: href,
      })
    }
  }

  const ModalHeaderContent =
    activeSubmenu === 'smMain' ? (
      <React.Fragment>
        <DialogTitle className="sr-only" asChild>
          <p>User menu</p>
        </DialogTitle>
        <div className="inline-block mt-[14px]">
          <a className="block h-fit" aria-label="Zumper Homepage" href="/">
            <ZumperFullDark size={36} />
          </a>
        </div>
      </React.Fragment>
    ) : (
      <div className="flex items-center justify-center absolute h-11 top-[10px] left-6 right-6">
        <button
          aria-label="Back"
          className="absolute left-0 top-[2px] text-left h-full w-11"
          onClick={showPrevMenu}
          type="button"
        >
          <ChevronLeft fill="#726F6C" size={12} />
        </button>
        <DialogTitle asChild>
          <p className="label14Caps">{titles[activeSubmenu]}</p>
        </DialogTitle>
      </div>
    )

  const ModalBodyContent = (
    <ModalBodyContentLoadable
      activeSubmenu={activeSubmenu}
      close={close}
      handleSetActiveSubmenu={handleSetActiveSubmenu}
      isMessage3={isMessage3}
      onLoginClick={onLoginClick}
      onLogoutClick={onLogoutClick}
      onSignupClick={onSignupClick}
      signInHref={signInHref}
      signUpHref={signUpHref}
      unreadCount={unreadCount}
      withCityTracking={withCityTracking}
    />
  )

  return (
    <React.Fragment>
      <Dialog
        open={showModal}
        onOpenChange={(open) => {
          if (!open) {
            close()
          }
        }}
      >
        <DialogContentWithOverlayAndCloseButton
          className="block z-[1400] bg-gray-50 p-0 overflow-scroll h-full"
          closeClassName="top-[10px] right-[14px] w-11 h-11"
          onOpenAutoFocus={(e) => e.preventDefault()}
          data-testid="header-mobile-menu"
        >
          <DialogHeader className="flex-row mx-6">{ModalHeaderContent}</DialogHeader>
          {showModal && <div className="pt-12 px-6 pb-[114px]">{ModalBodyContent}</div>}
        </DialogContentWithOverlayAndCloseButton>
      </Dialog>
      {!showModal && isBot && (
        <div className="hidden pointer-events-none relative">
          <Dialog>{ModalHeaderContent}</Dialog>
          <div>{ModalBodyContent}</div>
        </div>
      )}
    </React.Fragment>
  )
}
